// PermissionMatrix — checkbox grid for granting permissions to a role.
//
// CHG-1663 / ITER-3772 (Wave 1 / Tier 1 — Roles port).
//
// Renders module-grouped permission catalog with indeterminate
// module-level toggles. Consumed by role-detail's Permissions tab
// (wired via override-block:custom-tabs in role-detail.ts) and by
// the row-level "Edit permissions" action on the list page (via
// the page wrapper's headerActions override).

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  rolesApi,
  type PermissionInfo,
  type Role,
} from "../../api/roles";
import { ModalShell } from "./ModalShell";

export function PermissionMatrix({
  role,
  onClose,
}: {
  role: Role;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string> | null>(null);

  const catalogQuery = useQuery({
    queryKey: ["permissions", "catalog"] as const,
    queryFn: () => rolesApi.listPermissions(),
  });
  const currentQuery = useQuery({
    queryKey: ["role-permissions", role.id] as const,
    queryFn: () => rolesApi.getRolePermissions(role.id),
  });

  if (selected === null && currentQuery.data) {
    setSelected(new Set(currentQuery.data));
  }

  const grouped = useMemo(() => {
    const out: Record<string, PermissionInfo[]> = {};
    for (const p of catalogQuery.data ?? []) {
      const mod = p.module || p.code.split(".")[0] || "general";
      if (!out[mod]) out[mod] = [];
      out[mod].push(p);
    }
    return out;
  }, [catalogQuery.data]);

  const save = useMutation({
    mutationFn: () =>
      rolesApi.setRolePermissions(role.id, Array.from(selected ?? new Set())),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roles", "list"] });
      qc.invalidateQueries({ queryKey: ["roles", "detail"] });
      qc.invalidateQueries({ queryKey: ["role-permissions", role.id] });
      onClose();
    },
  });

  const toggle = (code: string) => {
    setSelected((prev) => {
      const next = new Set(prev ?? new Set<string>());
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const toggleModule = (mod: string) => {
    const codes = grouped[mod]?.map((p) => p.code) ?? [];
    setSelected((prev) => {
      const next = new Set(prev ?? new Set<string>());
      const allOn = codes.every((c) => next.has(c));
      for (const c of codes) {
        if (allOn) next.delete(c);
        else next.add(c);
      }
      return next;
    });
  };

  const loading =
    catalogQuery.isLoading || currentQuery.isLoading || selected === null;

  return (
    <ModalShell
      title={`Edit permissions — ${role.display_name}`}
      onClose={onClose}
      size="lg"
    >
      <div
        data-form="role-permissions"
        style={{
          padding: "16px 20px",
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 12,
          minHeight: 0,
        }}
      >
        {loading ? (
          <p
            data-testid="permissions-loading"
            style={{ fontSize: 13, color: "var(--color-text-muted)" }}
          >
            Loading permissions…
          </p>
        ) : Object.keys(grouped).length === 0 ? (
          <p style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
            No permissions defined.
          </p>
        ) : (
          Object.entries(grouped)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([mod, perms]) => {
              const all = perms.every((p) => selected!.has(p.code));
              const some = perms.some((p) => selected!.has(p.code));
              return (
                <div
                  key={mod}
                  data-module={mod}
                  style={{
                    border: "1px solid var(--color-border)",
                    borderRadius: "var(--radius-md, 8px)",
                    padding: 12,
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 8,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={all}
                      ref={(el) => {
                        if (el) el.indeterminate = some && !all;
                      }}
                      onChange={() => toggleModule(mod)}
                      data-action="toggle-module"
                    />
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--color-text)",
                        textTransform: "capitalize",
                      }}
                    >
                      {mod.replace(/-/g, " ")}
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        color: "var(--color-text-muted)",
                      }}
                    >
                      ({perms.filter((p) => selected!.has(p.code)).length}/
                      {perms.length})
                    </span>
                  </label>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                      gap: 4,
                      marginLeft: 24,
                    }}
                  >
                    {perms.map((p) => (
                      <label
                        key={p.code}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 6,
                          padding: "2px 0",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selected!.has(p.code)}
                          onChange={() => toggle(p.code)}
                          data-permission={p.code}
                          style={{ marginTop: 2 }}
                        />
                        <span style={{ minWidth: 0 }}>
                          <span
                            style={{
                              fontSize: 13,
                              color: "var(--color-text)",
                              display: "block",
                            }}
                          >
                            {p.display_name || p.code}
                          </span>
                          {p.description ? (
                            <span
                              style={{
                                fontSize: 11,
                                color: "var(--color-text-muted)",
                                display: "block",
                              }}
                            >
                              {p.description}
                            </span>
                          ) : null}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })
        )}
      </div>
      <footer
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          padding: "12px 20px",
          borderTop: "1px solid var(--color-border-subtle)",
        }}
      >
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onClose}
          disabled={save.isPending}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => save.mutate()}
          disabled={loading || save.isPending}
          data-action="save-permissions"
        >
          {save.isPending ? "Saving…" : "Save permissions"}
        </button>
      </footer>
    </ModalShell>
  );
}
